import { ArrowPathIcon } from "@heroicons/react/24/solid";
import Button from "../../components/Button";
import Panel from "./Panel";
import Table from "../../components/Table";
import { getTerminalsTableColumns } from "./terminalsTableColumns";
import { Terminal } from "../../services/accountService";
import useRefreshTerminalListMutation from "../../hooks/data/mutations/useRefreshTerminalListMutation";
import { useSnackbar } from "../../components/Snackbar";
import { useEffect, useState } from "react";
import useUpdateTerminalMutation from "../../hooks/data/mutations/useUpdateTerminalMutation";
import HasAccess, { csrAdminRoles } from "../../components/HasAccess";

export type TerminalsTableMeta = {
  currentEditingTerminal: Terminal | null;
  setCurrentEditingTerminal: (terminal: Terminal) => void;
  onTerminalEditStart: (terminal: Terminal) => void;
  onTerminalUpdate: () => void;
  onTerminalUpdateCanceled: () => void;
  isUpdatingTerminal: boolean;
};

type Props = {
  accountHolderCode: string;
  terminals: Terminal[];
  isLoading: boolean;
};

function Terminals({
  accountHolderCode,
  terminals,
  isLoading,
}: Props): JSX.Element {
  const [currentEditingTerminal, setCurrentEditingTerminal] =
    useState<Terminal | null>(null);
  const { mutate: refreshTerminalList, isLoading: isRefreshingTerminalList } =
    useRefreshTerminalListMutation(accountHolderCode);
  const { mutate: updateTerminal, isLoading: isUpdatingTerminal } =
    useUpdateTerminalMutation(accountHolderCode);
  const { openSnackbar } = useSnackbar({
    duration: 30000,
    position: "top",
  });

  function handleRefreshClick() {
    refreshTerminalList(accountHolderCode, {
      onError(e) {
        openSnackbar(e, {
          type: "error",
        });
      },
    });
  }

  function handleTerminalEditStart(terminal: Terminal) {
    setCurrentEditingTerminal(terminal);
  }

  function handleTerminalUpdate() {
    updateTerminal(currentEditingTerminal!, {
      onSuccess() {
        openSnackbar(
          `Terminal ${currentEditingTerminal?.friendlyName} successfully updated.`,
          {
            type: "success",
          }
        );
        setCurrentEditingTerminal(null);
      },
      onError(e) {
        openSnackbar(e, {
          type: "error",
        });
      },
    });
  }

  function handleTerminalUpdateCanceled() {
    setCurrentEditingTerminal(null);
  }

  return (
    <Panel className="col-span-12">
      <Panel.Header title="Terminals">
        <HasAccess allowedRoles={[...csrAdminRoles]}>
          <Button
            size="sm"
            onClick={handleRefreshClick}
            disabled={isLoading || isRefreshingTerminalList}
            icon={
              <ArrowPathIcon
                className={isRefreshingTerminalList ? "animate-spin" : ""}
              />
            }
          >
            Refresh
          </Button>
        </HasAccess>
      </Panel.Header>
      <Panel.Content>
        <Table
          id="terminals"
          data={terminals}
          columns={getTerminalsTableColumns()}
          showPaginator={false}
          isLoading={isLoading || isRefreshingTerminalList}
          noRowsText="There are no terminals to display."
          tableClasses="w-auto"
          meta={
            {
              currentEditingTerminal,
              setCurrentEditingTerminal,
              onTerminalEditStart: handleTerminalEditStart,
              onTerminalUpdate: handleTerminalUpdate,
              onTerminalUpdateCanceled: handleTerminalUpdateCanceled,
              isUpdatingTerminal,
            } as TerminalsTableMeta
          }
        />
      </Panel.Content>
    </Panel>
  );
}

export default Terminals;
